/** Longitud maxima de un id (coincide con `IdSchema` de los contratos). */
export const MAX_ID_LENGTH = 128;

/**
 * Convierte un nombre visible en un fragmento de id: sin acentos, en minusculas y con
 * cualquier caracter fuera de `[a-z0-9]` sustituido por `_`.
 *
 * @example toIdSlug('Año Fiscal') // 'ano_fiscal'
 */
export function toIdSlug(name: string, fallback = 'item'): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug.length > 0 ? slug : fallback;
}

/** Hash FNV-1a de 32 bits en base 36; estable entre ejecuciones y plataformas. */
export function stableHash(text: string, length = 6): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36).padStart(length, '0').slice(-length);
}

/** Recorta un id a `MAX_ID_LENGTH` conservando unicidad con un hash del id completo. */
export function fitId(id: string): string {
  if (id.length <= MAX_ID_LENGTH) return id;
  return `${id.slice(0, MAX_ID_LENGTH - 9)}_${stableHash(id, 8)}`;
}

export interface IdAllocator {
  /** Devuelve un id unico dentro del alcance para el nombre visible dado. */
  allocate(name: string, fallback?: string): string;
}

/**
 * Asigna ids deterministas y sin colisiones dentro de un alcance (una fuente, una tabla).
 * El nombre visible y el id son conceptos distintos: el id nunca se muestra y no cambia
 * mientras el nombre y el orden fisico se mantengan.
 *
 * - Nombres distintos con el mismo slug: se agrega un hash del nombre original.
 * - Nombres identicos repetidos: se agrega un ordinal (`_2`, `_3`...).
 *
 * @example
 * const ids = createIdAllocator('ventas');
 * ids.allocate('Ventas 2024'); // 'ventas.ventas_2024'
 * ids.allocate('Ventas 2024'); // 'ventas.ventas_2024_2'
 * ids.allocate('Ventas-2024'); // 'ventas.ventas_2024_<hash>'
 */
export function createIdAllocator(prefix?: string): IdAllocator {
  const owners = new Map<string, string>();
  return {
    allocate(name, fallback) {
      const slug = toIdSlug(name, fallback);
      const base = fitId(prefix === undefined ? slug : `${prefix}.${slug}`);
      let candidate = base;
      if (owners.has(candidate) && owners.get(base) !== name) {
        candidate = fitId(`${base}_${stableHash(name)}`);
      }
      for (let ordinal = 2; owners.has(candidate); ordinal++) {
        candidate = fitId(`${base}_${String(ordinal)}`);
      }
      owners.set(candidate, name);
      return candidate;
    },
  };
}
